import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import {
  getReferencePackAccessDurationLabel,
  ReferencePackCard,
  type ReferencePackCardData,
} from '../reference-packs/ReferencePackCard';

interface InvitationSummary {
  name: string;
  code: string;
  scheduledAt: string | null;
  imageUrl: string | null;
  hostName: string | null;
  modelName: string | null;
}

interface ReferencePackSummaryProduct {
  id: string;
  title: string;
  description: string | null;
  priceCents: number | null;
  currency: string | null;
  coverImageUrl: string | null;
  captureIds: string[];
  recordingIds: string[];
  license: string | null;
  accessDurationCount: number | null;
  accessDurationUnit: 'days' | 'weeks' | 'months' | 'years' | null;
  accessExpiresOn: string | null;
  captures?: Array<{ imageUrl?: string | null; thumbnailUrl?: string | null }>;
}

function toReferencePackCard(product: ReferencePackSummaryProduct): ReferencePackCardData {
  const firstCaptureUrl = product.captures?.find((capture) => capture.imageUrl || capture.thumbnailUrl);

  return {
    title: product.title,
    description: product.description,
    priceCents: product.priceCents,
    currency: product.currency,
    coverImageUrl: product.coverImageUrl || firstCaptureUrl?.imageUrl || firstCaptureUrl?.thumbnailUrl || null,
    photoCount: product.captureIds.length,
    videoCount: product.recordingIds.length,
    license: product.license,
    accessDurationLabel: getReferencePackAccessDurationLabel(product.accessDurationCount, product.accessDurationUnit),
    accessExpiresOn: product.accessExpiresOn,
  };
}

export function RecordingRequestPage() {
  const { code } = useParams<{ code: string }>();
  const { getToken, isSignedIn } = useAuth();
  const [session, setSession] = useState<InvitationSummary | null>(null);
  const [referencePack, setReferencePack] = useState<ReferencePackSummaryProduct | null>(null);
  const [accessExpiresOn, setAccessExpiresOn] = useState('');
  const [intendedUse, setIntendedUse] = useState('');
  const [artistNote, setArtistNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const todayDate = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    const fetchSession = async () => {
      if (!code) return;

      try {
        const response = await fetch(`/api/rooms/${code}/invitation`);
        if (response.ok) {
          const data = await response.json();
          setSession({
            name: data.name,
            code: data.code,
            scheduledAt: data.scheduledAt ?? null,
            imageUrl: data.imageUrl ?? null,
            hostName: data.hostProfile?.displayName ?? data.hostName ?? null,
            modelName: data.modelProfile?.displayName ?? data.modelName ?? null,
          });
        }

        const packResponse = await fetch(`/api/products/reference-packs/sessions/${code}/summary`);
        if (packResponse.ok) {
          const packData = await packResponse.json();
          setReferencePack(packData.products?.[0] ?? null);
        }
      } catch (err) {
        console.error('Error loading session:', err);
      }
    };

    fetchSession();
  }, [code]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code) return;
    if (!isSignedIn) {
      setError('Please sign in to request recordings.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/recordings/${code}/requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessExpiresOn,
          intendedUse,
          artistNote,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to request recordings');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request recordings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-bg pt-[72px]">
      <NavBar variant="light" />

      <main className="flex-1 py-12 px-6">
        <div className="max-w-[560px] mx-auto bg-panel border border-line rounded-xl p-6">
          {code && (
            <Link to={`/s/${code}/summary`} className="mb-6 inline-flex text-sm text-text-dim hover:text-text transition-colors">
              Back to session
            </Link>
          )}
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-soft flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h1 className="font-display text-2xl font-light text-text mb-2">Request sent</h1>
              <p className="text-text-dim mb-6">
                We have recorded your request for {session?.name || 'this session'}.
              </p>
              {code && (
                <Link to={`/s/${code}/summary`} className="btn btn-accent">
                  Return to session
                </Link>
              )}
            </div>
          ) : (
            <>
              {session && (
                <section className="mb-6 pb-6 border-b border-line">
                  {referencePack ? (
                    <ReferencePackCard
                      href={`/dashboard/packs/${referencePack.id}`}
                      pack={toReferencePackCard(referencePack)}
                    />
                  ) : (
                    <>
                      {session.imageUrl && (
                        <img
                          src={session.imageUrl}
                          alt={session.name}
                          className="w-full aspect-video object-cover rounded-lg bg-bg-secondary mb-5"
                        />
                      )}
                      <h2 className="font-display text-xl font-light text-text">
                        {session.name}
                      </h2>
                    </>
                  )}
                </section>
              )}

              <h1 className="font-display text-2xl font-light text-text mb-2">
                Request Recordings
              </h1>
              <p className="text-text-dim mb-6">
                Recordings of nude sessions create potential vulnerabilities for the model, so we ask artists to give context around how those recordings will be used.
              </p>

              {!isSignedIn ? (
                <div className="bg-bg-secondary border border-line rounded-lg p-4">
                  <p className="text-sm text-text-dim mb-4">Please sign in so we know who should receive access.</p>
                  <Link to={`/sign-in?redirect_url=/s/${code}/recording`} className="btn btn-accent">
                    Sign in
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-[10px] text-text-faint font-mono uppercase mb-2">
                      Recording access expires
                    </label>
                    <input
                      type="date"
                      value={accessExpiresOn}
                      onChange={(event) => setAccessExpiresOn(event.target.value)}
                      min={todayDate}
                      className="form-input w-full"
                      required
                    />
                    <p className="mt-2 text-[12px] text-text-dim">
                      After this date, your playback access will no longer be available.
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] text-text-faint font-mono uppercase mb-2">
                      How will the files be used?
                    </label>
                    <textarea
                      value={intendedUse}
                      onChange={(event) => setIntendedUse(event.target.value)}
                      rows={4}
                      maxLength={1000}
                      placeholder="For example: private drawing reference, a class assignment, portfolio study, or teaching review."
                      className="form-input w-full resize-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] text-text-faint font-mono uppercase mb-2">
                      Note to the artist
                    </label>
                    <textarea
                      value={artistNote}
                      onChange={(event) => setArtistNote(event.target.value)}
                      rows={4}
                      maxLength={1000}
                      placeholder="Add any extra context or request details."
                      className="form-input w-full resize-none"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-warn">{error}</p>
                  )}

                  <button type="submit" disabled={loading} className="btn btn-accent w-full">
                    {loading ? 'Sending...' : 'Submit request'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}
